import { useApiBase } from './useApiBase'

/**
 * Cloud save API (#450). Session-scoped: the backend resolves the save scope
 * (app identity) from the session and the user from the JWT. See
 * doc/architecture/cloud_save.md.
 */
export interface SavedataFile {
  filename: string
  /** base64-encoded bytes */
  content: string
  sizeBytes: number
  updatedAt?: string
}

export interface ListSavedataResponse {
  scope: string | null
  files: SavedataFile[]
}

export interface PutSavedataResponse {
  filename: string
  savedBytes: number
  updatedAt: string
}

export function useSavedataApi() {
  const { apiBase, authenticatedFetch } = useApiBase()

  /** Fetch all savedata files for the session's scope (content included). */
  const listSavedata = (sessionId: string) =>
    authenticatedFetch<ListSavedataResponse>(
      `${apiBase}/api/v1/sessions/${encodeURIComponent(sessionId)}/savedata`,
      { method: 'GET' },
    )

  /**
   * Upsert one savedata file. `filename` is a relative path under the save root
   * (e.g. "vendor/app/save.json") and may contain "/", so it goes in the body
   * (not the URL path).
   */
  const putSavedata = (sessionId: string, filename: string, contentBase64: string) =>
    authenticatedFetch<PutSavedataResponse>(
      `${apiBase}/api/v1/sessions/${encodeURIComponent(sessionId)}/savedata`,
      { method: 'PUT', body: JSON.stringify({ filename, content: contentBase64 }) },
    )

  return { listSavedata, putSavedata }
}
